import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    Checkbox,
    Text,
    VStack,
    HStack,
    Box,
    useToast,
    Icon,
} from "@chakra-ui/react";
import {
    IoCheckmarkCircle,
    IoSparklesOutline,
    IoArrowForward,
    IoTimeOutline,
    IoDocumentTextOutline,
    IoOpenOutline,
} from "react-icons/io5";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/firebase-config";
import { getStripeMode } from "../../lib/stripeMode";
import { useEffect, useState } from "react";
import producerAgreementPdf from "../../Assets/PDF/Proximy - Community Event Producer - Agreement.pdf";

const HOST_PERKS = [
    "Create unlimited events",
    "Set ticket prices and manage sales",
    "Track attendees and event analytics",
    "Receive payments directly",
    "Build your community",
];

const PRODUCER_AGREEMENT_VERSION = "community-event-producer-agreement-2026-04-29";

export const BecomeHostModal = ({ isOpen, onClose, user, setIsHost, returnPath }) => {
    const [loading, setLoading] = useState(false);
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (isOpen) {
            setAgreementAccepted(false);
            setShowAgreement(false);
        }
    }, [isOpen]);

    const handleBecomeHost = async () => {
        if (!user) return;
        if (!agreementAccepted) {
            toast({
                title: "Agreement required",
                description:
                    "Please review and agree to the Community Event Producer Agreement before continuing.",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const mode = getStripeMode();
        setLoading(true);
        try {
            console.log(`🚀 Calling createStripeExpressAccount in ${mode} mode...`);

            const callable = httpsCallable(functions, "createStripeExpressAccount");
            const { data: result } = await callable({
                userId: user.uid,
                email: user.email,
                mode,
                appBaseUrl: window.location.origin,
                // Tells Stripe's post-onboarding redirect where to send the
                // user back to (e.g. the venue landing page), so that flow
                // can resume automatically instead of dropping them home.
                ...(returnPath ? { returnPath } : {}),
                agreementConsent: {
                    accepted: true,
                    agreementVersion: PRODUCER_AGREEMENT_VERSION,
                    agreementName: "Community Event Producer Agreement & Terms and Conditions",
                    agreementUrl: producerAgreementPdf,
                    acceptedAtClient: new Date().toISOString(),
                },
            });

            console.log("📦 Function result:", result);

            if (result?.success && result?.onboardingUrl) {
                console.log("✅ Redirecting to:", result.onboardingUrl);
                window.location.href = result.onboardingUrl;
            } else {
                console.error("❌ Function returned success=false");
                throw new Error("Failed to create Stripe account");
            }
        } catch (error) {
            console.error("❌ Error starting host onboarding:", error);
            toast({
                title: "Error",
                description:
                    error.message ||
                    "Failed to start onboarding. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={{ base: "full", md: "md" }}
            isCentered
            scrollBehavior="inside"
        >
            <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.500" />
            <ModalContent
                borderRadius={{ base: 0, md: "2xl" }}
                overflow="hidden"
            >
                <ModalHeader
                    pb={2}
                    pt={5}
                    px={6}
                    borderBottom="1px solid"
                    borderColor="gray.100"
                >
                    <HStack spacing={3} align="center">
                        <Box
                            bg="blue.50"
                            p={2}
                            borderRadius="lg"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Icon
                                as={IoSparklesOutline}
                                color="#508cf7"
                                boxSize={4}
                            />
                        </Box>
                        <Box>
                            <Text
                                fontSize="lg"
                                fontWeight="700"
                                color="gray.800"
                            >
                                Become a Host
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                                Start creating and selling tickets to your events
                            </Text>
                        </Box>
                    </HStack>
                    <ModalCloseButton top={4} right={4} />
                </ModalHeader>

                <ModalBody px={6} py={5}>
                    <VStack spacing={5} align="stretch">
                        <Box
                            bgGradient="linear(to-br, blue.50, white)"
                            border="1px solid"
                            borderColor="blue.100"
                            borderRadius="xl"
                            p={4}
                        >
                            <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="0.05em"
                                mb={3}
                            >
                                What you'll unlock
                            </Text>
                            <VStack align="stretch" spacing={2}>
                                {HOST_PERKS.map((perk) => (
                                    <HStack key={perk} spacing={2} align="flex-start">
                                        <Icon
                                            as={IoCheckmarkCircle}
                                            color="#508cf7"
                                            boxSize={4}
                                            mt="2px"
                                            flexShrink={0}
                                        />
                                        <Text fontSize="sm" color="gray.700">
                                            {perk}
                                        </Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>

                        <HStack
                            spacing={3}
                            bg="orange.50"
                            border="1px solid"
                            borderColor="orange.200"
                            borderRadius="lg"
                            px={3}
                            py={3}
                            align="flex-start"
                        >
                            <Icon
                                as={IoTimeOutline}
                                color="orange.500"
                                boxSize={4}
                                mt="2px"
                                flexShrink={0}
                            />
                            <Box>
                                <Text
                                    fontSize="xs"
                                    color="orange.700"
                                    fontWeight="600"
                                >
                                    Next: Stripe payout setup
                                </Text>
                                <Text fontSize="11px" color="orange.700" mt="1px">
                                    We'll send you to Stripe to verify your
                                    account so we can pay out ticket sales. Takes
                                    about 2–3 minutes.
                                </Text>
                            </Box>
                        </HStack>

                        <Box
                            border="1px solid"
                            borderColor={agreementAccepted ? "blue.200" : "gray.200"}
                            borderRadius="xl"
                            p={4}
                            bg={agreementAccepted ? "blue.50" : "white"}
                        >
                            <HStack align="flex-start" spacing={3}>
                                <Checkbox
                                    isChecked={agreementAccepted}
                                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                                    colorScheme="blue"
                                    mt="2px"
                                    isDisabled={loading}
                                />
                                <Box flex={1}>
                                    <Text fontSize="sm" fontWeight="600" color="gray.800">
                                        I agree to the Community Event Producer Agreement & Terms and Conditions
                                    </Text>
                                    <HStack spacing={3} mt={2} flexWrap="wrap">
                                        <Button
                                            size="xs"
                                            variant="link"
                                            color="#508cf7"
                                            fontWeight="700"
                                            leftIcon={<Icon as={IoDocumentTextOutline} boxSize={3.5} />}
                                            onClick={() => setShowAgreement((prev) => !prev)}
                                        >
                                            {showAgreement ? "Hide agreement" : "View agreement"}
                                        </Button>
                                        <Button
                                            as="a"
                                            href={producerAgreementPdf}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="xs"
                                            variant="link"
                                            color="gray.500"
                                            fontWeight="600"
                                            leftIcon={<Icon as={IoOpenOutline} boxSize={3.5} />}
                                        >
                                            Open PDF
                                        </Button>
                                    </HStack>
                                </Box>
                            </HStack>

                            {showAgreement && (
                                <Box
                                    mt={4}
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="lg"
                                    overflow="hidden"
                                    bg="gray.50"
                                    h={{ base: "360px", md: "420px" }}
                                >
                                    <Box
                                        as="iframe"
                                        title="Community Event Producer Agreement"
                                        src={producerAgreementPdf}
                                        w="100%"
                                        h="100%"
                                        border="0"
                                    />
                                </Box>
                            )}
                        </Box>

                        <Text
                            fontSize="11px"
                            color="gray.400"
                            textAlign="center"
                        >
                            It's free to become a host.
                        </Text>
                    </VStack>
                </ModalBody>

                <ModalFooter
                    borderTop="1px solid"
                    borderColor="gray.100"
                    py={4}
                    px={6}
                >
                    <Button
                        size="sm"
                        variant="ghost"
                        fontWeight="600"
                        onClick={onClose}
                        mr={2}
                        isDisabled={loading}
                    >
                        Not now
                    </Button>
                    <Button
                        size="sm"
                        bg="#508cf7"
                        color="white"
                        fontWeight="600"
                        borderRadius="lg"
                        px={5}
                        rightIcon={<Icon as={IoArrowForward} boxSize={4} />}
                        _hover={{ bg: "#226cf1" }}
                        onClick={handleBecomeHost}
                        isLoading={loading}
                        isDisabled={!agreementAccepted}
                        loadingText="Setting up…"
                    >
                        Continue
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
